import type { Phrases } from '../common/Phrases.d';
import type { CommonSuccessPageParams } from '../common/CommonSuccessPageParams.d';
import {
	type Reaction,
	type NumberOfPeopleGotReactions,
	type Analyzed,
	type AnalyzedHow,
	type IfNaloxoneWorked,
	type ServicesWhere,
	type Who,
	FIELD_VALUE_ANALYZED,
	FIELD_VALUE_IF_NALOXONE_WORKED,
	SERVICES_WHERE,
} from './constants';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { Link } from '@hdir/ui/link';
import React, { type FC, useEffect, useRef } from 'react';
import { Span } from '../../../components/Element';
import { P } from '../../../components/Paragraph';
import { FakeButton } from '../../../components/FakeButton';
import { Row, Stack } from '../../../components/Flex';
import { DateComponent } from '../../../components/DateComponent';
import { Time } from '../../../components/Time';
import { H3 } from '../../../components/heading/Heading';
import { FontAwesomeIconWrapper as Icon } from '../../../components/FontAwesomeIconWrapper';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@hdir/ui/accordion';
import { sanitizeHtml } from '../../../shared/sanitizeHtml';
import {
	SuccessPageAdditionalInformation,
	SuccessPageCapsulePowderColors,
	SuccessPageColors,
	SuccessPageHeader1,
	SuccessPageImages,
	SuccessPageInfobox,
	SuccessPageIntoxicantDescription,
	SuccessPagePillShape,
	SuccessPageSubstance,
	SuccessPageSubstanceForm,
} from '../common/SuccessPage';


interface SuccessPageParams extends CommonSuccessPageParams {
	analyzed?: Analyzed;
	analyzedHow?: AnalyzedHow;
	analyzedResult?: string;
	email?: string;
	ifNaloxoneWorked?: IfNaloxoneWorked;
	mobileNumber?: string;
	numberOfPeopleGotReactions?: NumberOfPeopleGotReactions;
	numberOfPeopleGotReactionsOther?: string;
	reactions?: Reaction[];
	reactionsOther?: string;
	whatAgencyInstitutionOrOrganization?: string;
	whenDidTheIncidentHappen?: Date;
	whenDidTheIncidentHappenOther?: string;
	where: ServicesWhere;
	who: Who;
}


const translateWhere = ({
	phrases,
	where,
}: {
	phrases: Phrases;
	where?: string;
}): string => where === SERVICES_WHERE.OTHER
	? phrases['notificationForm.services.whereField.value.other']
	: '';


const OTHER: ServicesWhere[] = [SERVICES_WHERE.OTHER];

export const SuccessPageWhere = ({
	municipalityName,
	notFilledOut,
	phrases,
	where,
	whereOther,
}: {
	municipalityName?: string;
	notFilledOut: string;
	phrases: Phrases;
	where: ServicesWhere;
	whereOther?: string;
}): React.JSX.Element => (
	<Stack className='gap-md'>
		<H3>{phrases['notificationForm.anonymous.whereField.label']}</H3>
		<p>{`${municipalityName || translateWhere({
			phrases,
			where,
		})}${
			whereOther ? `: ${whereOther}`
				: OTHER.includes(where) ? ` ${notFilledOut}` : ''
		}`}</p>
	</Stack>
);


export const SuccessPage: FC<SuccessPageParams> = ({
	additionalInformation,
	analyzed,
	analyzedHow,
	analyzedResult,
	capsulePowderColors,
	capsulePowderColorOther,
	colors,
	colorOther,
	email,
	ifNaloxoneWorked,
	imageDataUrls,
	intoxicantDescription,
	intoxicantOptions,
	mobileNumber,
	municipalityName,
	numberOfPeopleGotReactions,
	numberOfPeopleGotReactionsOther,
	phrases,
	pillShape,
	pillShapeOther,
	reactions,
	reactionsOther,
	sitePageUrl,
	submissionDate,
	substance,
	substanceForm,
	substanceOther,
	translatedColors,
	whatAgencyInstitutionOrOrganization,
	whenDidTheIncidentHappen,
	whenDidTheIncidentHappenOther,
	where,
	whereOther,
	who,
}) => {
	const notFilledOut = phrases['notificationForm.term.notFilledOut'];

	const h1Ref = useRef<HTMLHeadingElement>();

	useEffect(() => {
		if (h1Ref.current) {
			h1Ref.current.scrollIntoView({ behavior: 'instant', block: 'start' });
			h1Ref.current.focus();
		}
	}, []);

	return (
		<Stack className='gap-lg' padding={{ block: 'lg' }}>
			<SuccessPageHeader1
				h1Ref={h1Ref}
				phrases={phrases}
			/>
			<SuccessPageInfobox phrases={phrases}/>
			<Accordion>
				<AccordionItem>
					<AccordionTrigger>
						<Span
							dangerouslySetInnerHTML={{
								__html: sanitizeHtml(
									phrases[
										'notificationForm.services.successPage.accordionTrigger.html'
									],
								),
							}}
						/>
					</AccordionTrigger>
					<AccordionContent>
						<Stack className='gap-lg'>

							<Time dateTime={submissionDate}/>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.whoField.label']}</H3>

								<Row className='gap-md items-center'>
									{/* I guess this is safe as long as types match i18n */}
									<FakeButton>{phrases[`notificationForm.services.whoField.value.${who}`]}</FakeButton>
									{whatAgencyInstitutionOrOrganization}
								</Row>
							</Stack>

							<SuccessPageSubstance
								intoxicantOptions={intoxicantOptions}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substance={substance}
								substanceOther={substanceOther}
							/>

							<SuccessPageWhere
								municipalityName={municipalityName}
								notFilledOut={notFilledOut}
								phrases={phrases}
								where={where}
								whereOther={whereOther}
							/>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.whenField.label']}</H3>
								{
									whenDidTheIncidentHappen
										? <DateComponent date={whenDidTheIncidentHappen}/>
										: null
								}
								<P>{whenDidTheIncidentHappenOther}</P>
							</Stack>

							<SuccessPageSubstanceForm phrases={phrases} substanceForm={substanceForm}/>
							<SuccessPagePillShape
								notFilledOut={notFilledOut}
								phrases={phrases}
								pillShape={pillShape}
								pillShapeOther={pillShapeOther}
							/>
							<SuccessPageColors
								colorOther={colorOther}
								colors={colors}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substanceForm={substanceForm}
								translatedColors={translatedColors}
							/>
							<SuccessPageCapsulePowderColors
								capsulePowderColors={capsulePowderColors}
								capsulePowderColorOther={capsulePowderColorOther}
								notFilledOut={notFilledOut}
								phrases={phrases}
								translatedColors={translatedColors}
							/>

							<SuccessPageIntoxicantDescription
								intoxicantDescription={intoxicantDescription}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substanceForm={substanceForm}
							/>

							<SuccessPageImages
								imageDataUrls={imageDataUrls}
								phrases={phrases}
							/>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.reactionsField.label']}</H3>
								<Row className='gap-md items-center'>
									{
										reactions?.map((reaction, i) => <FakeButton key={`reaction-${i}`}>{
											phrases[`notificationForm.services.reactionsField.value.${reaction}`]
										}</FakeButton>)
									}
									<Span>{reactionsOther}</Span>
								</Row>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.numberOfPeopleGotReactionsField.label']}</H3>
								<Row className='gap-md items-center'>
									<FakeButton>
										{
											numberOfPeopleGotReactions
												? phrases[`notificationForm.services.numberOfPeopleGotReactionsField.value.${numberOfPeopleGotReactions}`]
												: ''
										}
									</FakeButton>
									{numberOfPeopleGotReactionsOther}
								</Row>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.analyzedField.label']}</H3>
								<FakeButton>
									{
										analyzed === FIELD_VALUE_ANALYZED.YES ? phrases['notificationForm.term.yes']
											: analyzed === FIELD_VALUE_ANALYZED.NO ? phrases['notificationForm.term.no']
												: ''
									}
								</FakeButton>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.analyzedHowField.label']}</H3>
								<FakeButton>
									{
										analyzedHow
											? phrases[`notificationForm.services.analyzedHowField.value.${analyzedHow}`]
											: ''
									}
								</FakeButton>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.analyzedResultField.label']}</H3>
								{
									analyzedResult
								}
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.ifNaloxoneWorkedField.label']}</H3>
								<FakeButton>
									{
										ifNaloxoneWorked === FIELD_VALUE_IF_NALOXONE_WORKED.YES ? phrases['notificationForm.term.yes']
											: ifNaloxoneWorked === FIELD_VALUE_IF_NALOXONE_WORKED.NO ? phrases['notificationForm.term.no']
												: ifNaloxoneWorked === FIELD_VALUE_IF_NALOXONE_WORKED.NALOXONE_NOT_GIVEN ? phrases['notificationForm.services.ifNaloxoneWorkedField.value.naloxoneNotGiven']
													: ''
									}
								</FakeButton>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.emailField.label']}</H3>
								<P>{email}</P>
							</Stack>

							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.services.mobileNumberField.label']}</H3>
								<P>{mobileNumber}</P>
							</Stack>

							<SuccessPageAdditionalInformation
								additionalInformation={additionalInformation}
								notFilledOut={notFilledOut}
								phrases={phrases}
							/>

						</Stack>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
			<Link variant="button-secondary" href={sitePageUrl}>Gå til forsiden <Icon icon={faArrowRight}/></Link>
		</Stack>
	);
};
